import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { ANIMALS_API_ENDPOINTS } from '../constants/apiEndpoints';
import { appContextDelete, appContextGet, appContextPost, appContextPut } from './httpCommon/appAxios.service';
import { Animal, AnimalDocument, AnimalDocumentSasUrlResponse } from '../types/interfaces/animal.interfaces';
import { ODataParams, PagedResponse, StandardParams, GetAccountAnimalParams } from '../types/interfaces/apiParams.interfaces';
import { Attachment } from '../types/interfaces';

export const createAnimal = (animal: Animal): Promise<AxiosResponse<number, any>> => {
  return appContextPost(ANIMALS_API_ENDPOINTS.CREATE, animal);
};

export const deleteMultipleAnimalAttachments = (animalId: string, attachmentIds: number[]): Promise<AxiosResponse<{}, any>> => {
  return appContextDelete(ANIMALS_API_ENDPOINTS.ANIMALS + animalId + ANIMALS_API_ENDPOINTS.ATTACHMENTS, {}, attachmentIds);
};

export const editAnimal = (id: string, animal: Animal, params?: {}): Promise<AxiosResponse<{}, any>> => {
  return appContextPut(ANIMALS_API_ENDPOINTS.EDIT + `/${id}`, animal, params);
};

export const getAccountAnimals = (
  accountId: number,
  params?: GetAccountAnimalParams,
): Promise<AxiosResponse<Animal[] | PagedResponse<Animal>, any>> => {
  return appContextGet(ANIMALS_API_ENDPOINTS.GET_ACCOUNT_ + accountId, params);
};

export const getAccountAnimalsOdata = (
  accountId: number,
  params?: ODataParams,
): Promise<AxiosResponse<Animal[] | PagedResponse<Animal>, any>> => {
  return appContextGet(ANIMALS_API_ENDPOINTS.GET_ACCOUNT_ + accountId + ANIMALS_API_ENDPOINTS.ODATA, params);
};

export const getAllAccountAnimals = (
  accountId: number,
  params?: GetAccountAnimalParams,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<Animal[] | PagedResponse<Animal>, any>> => {
  return appContextGet(
    ANIMALS_API_ENDPOINTS.GET_ACCOUNT_ + accountId + ANIMALS_API_ENDPOINTS.INVENTORY_OWNERSHIP,
    params,
    config,
  );
};

export const getAllAnimalAttachments = (animalId: string, params?: {}): Promise<AxiosResponse<Attachment[], any>> => {
  return appContextGet(ANIMALS_API_ENDPOINTS.ANIMALS + animalId + ANIMALS_API_ENDPOINTS.ATTACHMENTS, params);
};

export const getAnimalAttachment = (
  animalId: string,
  attachmentId: number,
  accessToken: string,
): Promise<AxiosResponse<{}, any>> => {
  return appContextGet(
    ANIMALS_API_ENDPOINTS.ANIMALS + animalId + ANIMALS_API_ENDPOINTS.ATTACHMENTS + `/${attachmentId}/` + accessToken,
  );
};

export const getAnimalDocuments = (animalId: string, params?: {}): Promise<AxiosResponse<AnimalDocument[], any>> => {
  return appContextGet(ANIMALS_API_ENDPOINTS.ANIMALS + animalId + ANIMALS_API_ENDPOINTS.DOCUMENTS, params);
};

export const getAnimalDocumentSasUrl = (
  animalId: string,
  attachmentId: number,
  accessToken: string,
): Promise<AxiosResponse<AnimalDocumentSasUrlResponse, any>> => {
  return appContextGet(
    ANIMALS_API_ENDPOINTS.ANIMALS +
      animalId +
      ANIMALS_API_ENDPOINTS.DOCUMENTS +
      `/${attachmentId}` +
      ANIMALS_API_ENDPOINTS.SAS_URL,
    undefined,
    { headers: { 'x-access-token': accessToken } },
  );
};

export const deleteAnimalDocument = (animalId: string, attachmentId: number): Promise<AxiosResponse<{}, any>> => {
  return appContextDelete(ANIMALS_API_ENDPOINTS.ANIMALS + animalId + ANIMALS_API_ENDPOINTS.DOCUMENTS + `/${attachmentId}`);
};

export const uploadAnimalDocument = (animalId: string, documentFile: FormData): Promise<AxiosResponse<{}, any>> => {
  return appContextPost(ANIMALS_API_ENDPOINTS.ANIMALS + animalId + ANIMALS_API_ENDPOINTS.DOCUMENTS, documentFile);
};

export const getAnimalById = (id: string, params?: { include: string }): Promise<AxiosResponse<Animal, any>> => {
  return appContextGet(ANIMALS_API_ENDPOINTS.GET + `/${id}`, params);
};

export const getAnimalCount = (params?: { filter: string }): Promise<AxiosResponse<{}, any>> => {
  return appContextGet(ANIMALS_API_ENDPOINTS.GET_COUNT, params);
};

export const getAnimals = (params?: StandardParams): Promise<AxiosResponse<Animal[] | PagedResponse<Animal>, any>> => {
  return appContextGet(ANIMALS_API_ENDPOINTS.GET, params);
};

export const setGlobalThreshold = (thresholdValue: number): Promise<AxiosResponse<{}, any>> => {
  return appContextPut(ANIMALS_API_ENDPOINTS.SET_GLOBAL_THRESHOLD, {}, { threshold: thresholdValue });
};

export const updateAnimalExportReady = (animalId: string, isExportReady: boolean): Promise<AxiosResponse<{}, any>> => {
  return appContextPut(ANIMALS_API_ENDPOINTS.UPDATE_IS_EXPORT_READY, {}, { animalId, isExportReady });
};

export const uploadAnimalAttachment = (animalId: string, attachmentFiles: FormData): Promise<AxiosResponse<{}, any>> => {
  return appContextPost(ANIMALS_API_ENDPOINTS.ANIMALS + animalId + ANIMALS_API_ENDPOINTS.ATTACHMENTS, attachmentFiles);
};
